import React from "react";
import { Field, FormRenderProps } from "@progress/kendo-react-form";
import { Button } from "@progress/kendo-react-buttons";
import { FaWhatsapp } from "react-icons/fa";
import { FiMessageSquare, FiTag } from "react-icons/fi";
import FormSelectionField from "../../../components/formFields/FormSelectionField";
import ShadowCard from "../../../components/common/ShadowCard";
import IconComponent from "../../../components/common/IconComponent";
import { requiredValidator } from "../../../components/formFields/CommonValidator";

const WhatsappIcon = FaWhatsapp as any;
const MessageIcon = FiMessageSquare as any;
const TagIcon = FiTag as any;

interface WhatsappComposerCardProps {
  apiTemplateOptions: { value: any; label: string; raw: any }[];
  selectedApiTemplate: any;
  activeVariables: number[];
  selectedCount: number;
  sending: boolean;
  isSendAccess: boolean;
  options: { label: string; value: string }[];
  onApiTemplateChange: (event: any, formRenderProps: FormRenderProps) => void;
  onGetSuggestions?: (
    e?: any,
    formRenderProps?: FormRenderProps,
    variableNumber?: number,
  ) => void;
  onSubmit: (values: any) => void;
  getFormValues: (formRenderProps: FormRenderProps) => any;
  formRenderProps: FormRenderProps;
}

export const WhatsappComposerCard: React.FC<WhatsappComposerCardProps> = ({
  apiTemplateOptions,
  selectedApiTemplate,
  activeVariables,
  selectedCount,
  sending,
  isSendAccess,
  options,
  onApiTemplateChange,
  onGetSuggestions,
  onSubmit,
  getFormValues,
  formRenderProps,
}) => {
  const isSendDisabled =
    sending || !selectedApiTemplate || selectedCount === 0 || !isSendAccess;

  return (
    <ShadowCard
      style={{
        padding: "18px",
        borderRadius: "16px",
        border: "1px solid #cdddf1ff",
        backgroundColor: "#f8fafc",
        display: "flex",
        flexDirection: "column",
        gap: "16px",
      }}
    >
      {/* Card Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          paddingBottom: "10px",
          borderBottom: "1px solid #f1f5f9",
        }}
      >
        <div
          style={{
            padding: "6px",
            borderRadius: "8px",
            backgroundColor: "#eff6ff",
            color: "#174583",
            display: "flex",
            alignItems: "center",
          }}
        >
          <IconComponent icon={MessageIcon} style={{ fontSize: "18px" }} />
        </div>
        <div>
          <h3
            style={{
              margin: 0,
              fontSize: "15px",
              fontWeight: 700,
              color: "#1e293b",
            }}
          >
            WhatsApp Configuration
          </h3>
          <p
            style={{ margin: "2px 0 0 0", fontSize: "11px", color: "#64748b" }}
          >
            Configure template and dynamic variable tags
          </p>
        </div>
      </div>

      {/* WhatsApp Template Dropdown */}
      <div style={{ display: "flex", flexDirection: "column" }}>
        <Field
          name="ApiTemplateID"
          label="Select WhatsApp Template"
          placeholder="Choose WhatsApp Template..."
          component={FormSelectionField}
          options={apiTemplateOptions}
          validator={requiredValidator}
          astrike={true}
          onChange={(e: any) => onApiTemplateChange(e, formRenderProps)}
        />
      </div>

      {/* Dynamic Variables Section */}
      {selectedApiTemplate && activeVariables.length > 0 && (
        <div
          style={{
            backgroundColor: "#ffffffff",
            borderRadius: "12px",
            padding: "14px",
            border: "1px solid #e2e8f0",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              marginBottom: "10px",
            }}
          >
            <IconComponent
              icon={TagIcon}
              style={{ color: "#25d366", fontSize: "15px" }}
            />
            <span
              style={{ fontSize: "13px", fontWeight: 700, color: "#334155" }}
            >
              Dynamic Variables ({activeVariables.length})
            </span>
          </div>

          {activeVariables.map((varNum: number) => (
            <Field
              key={varNum}
              name={`Variable${varNum}`}
              label={`Variable {{${varNum}}}`}
              placeholder="Choose variable mapping or type value..."
              component={FormSelectionField}
              allowCustom={true}
              options={
                formRenderProps.valueGetter(`variable_options_${varNum}`) ||
                options
              }
              validator={requiredValidator}
              astrike={true}
              customOnFilterChange={(e: any) =>
                onGetSuggestions?.(e, formRenderProps, varNum)
              }
            />
          ))}
        </div>
      )}

      {/* Send Bulk WhatsApp Button */}
      <div style={{ marginTop: "8px" }}>
        <Button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            const values = getFormValues(formRenderProps);
            onSubmit(values);
          }}
          disabled={isSendDisabled}
          style={{
            width: "100%",
            height: "44px",
            borderRadius: "10px",
            backgroundColor: isSendDisabled ? "#cbd5e1" : "#25d366",
            color: "#ffffff",
            fontWeight: 700,
            fontSize: "14px",
            border: "none",
            boxShadow: isSendDisabled
              ? "none"
              : "0 4px 14px rgba(37, 211, 102, 0.35)",
            cursor: isSendDisabled ? "not-allowed" : "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "12px",
            transition: "all 0.2s ease",
          }}
        >
          <IconComponent
            icon={WhatsappIcon}
            style={{ fontSize: "18px", marginRight: "6px" }}
          />
          <span>
            {sending
              ? "Sending Messages..."
              : `Send Bulk WhatsApp ${selectedCount > 0 ? `(${selectedCount} Contacts)` : ""}`}
          </span>
        </Button>
      </div>
    </ShadowCard>
  );
};
